(() => {
  'use strict';

  const ATTRIBUTE_NAME = 'Houderkeuze maandbloemen';
  const LINE_PROPERTY_NAME = 'Houdercombinatie bestelling';
  const HOST_SELECTOR = '[data-dovana-holder-picker-host]';
  const STYLE_ID = 'dovana-monthflower-holder-styles';

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .dovana-holder-picker {
        display: grid;
        gap: 10px;
        width: 100%;
        padding: 16px;
        margin-block: 14px;
        border: 1px solid rgb(var(--color-foreground-rgb) / 0.16);
        border-radius: max(8px, var(--style-border-radius-inputs, 8px));
        background: rgb(var(--color-foreground-rgb) / 0.035);
      }

      .dovana-holder-picker__title {
        margin: 0;
        font-weight: 700;
        font-size: var(--font-size--sm, 0.95rem);
      }

      .dovana-holder-picker__text,
      .dovana-holder-picker__status {
        margin: 0;
        font-size: var(--font-size--xs, 0.875rem);
        line-height: 1.45;
      }

      .dovana-holder-picker__select {
        width: 100%;
        min-height: 44px;
        padding: 10px 12px;
        color: var(--color-input-text, inherit);
        background: var(--color-input-background, transparent);
        border: var(--style-border-width-inputs, 1px) solid var(--color-input-border, currentColor);
        border-radius: var(--style-border-radius-inputs, 6px);
        font: inherit;
      }

      .dovana-holder-picker__status[data-state="required"],
      .dovana-holder-picker__status[data-state="error"] {
        font-weight: 600;
      }

      .dovana-holder-picker__status[data-state="saved"] {
        opacity: .72;
      }
    `;

    document.head.appendChild(style);
  }

  function rootPath() {
    return window.Shopify?.routes?.root || '/';
  }

  function combinations(total) {
    const found = [];

    function walk(remaining, maxPart, parts) {
      if (remaining === 0) {
        found.push(parts.slice());
        return;
      }

      const start = Math.min(4, maxPart, remaining);

      for (let size = start; size >= 1; size -= 1) {
        parts.push(size);
        walk(remaining - size, size, parts);
        parts.pop();
      }
    }

    walk(total, 4, []);

    found.sort((a, b) => {
      if (a.length !== b.length) return a.length - b.length;

      const length = Math.max(a.length, b.length);
      for (let i = 0; i < length; i += 1) {
        const av = a[i] || 0;
        const bv = b[i] || 0;
        if (av !== bv) return bv - av;
      }

      return 0;
    });

    return found;
  }

  function combinationLabel(parts) {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };

    for (const size of parts) counts[size] += 1;

    const labels = [];

    for (const size of [4, 3, 2, 1]) {
      const qty = counts[size];
      if (!qty) continue;

      if (size === 1) {
        labels.push(qty + '× ' + (qty === 1 ? 'enkele houder' : 'enkele houders'));
      } else {
        labels.push(qty + '× ' + size + '-bloemshouder');
      }
    }

    return labels.join(' + ');
  }

  async function saveCartAttribute(value) {
    const response = await fetch(rootPath() + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          [ATTRIBUTE_NAME]: value
        }
      })
    });

    if (!response.ok) {
      throw new Error('Houderkeuze kon niet als winkelwageninformatie worden opgeslagen.');
    }
  }

  async function saveCheckoutLineProperty(value, host) {
    const line = Number.parseInt(host.dataset.line || '0', 10) || 0;
    if (line <= 0) return;

    const cartResponse = await fetch(rootPath() + 'cart.js', {
      headers: { 'Accept': 'application/json' }
    });

    if (!cartResponse.ok) {
      throw new Error('Winkelwagen kon niet worden gelezen.');
    }

    const cart = await cartResponse.json();
    const item = cart.items?.[line - 1];

    if (!item) {
      throw new Error('Maandbloemregel kon niet worden gevonden.');
    }

    const properties = { ...(item.properties || {}) };

    if (value) {
      properties[LINE_PROPERTY_NAME] = value;
    } else {
      delete properties[LINE_PROPERTY_NAME];
    }

    if ((item.properties || {})[LINE_PROPERTY_NAME] === value) {
      return;
    }

    const response = await fetch(rootPath() + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        line,
        quantity: item.quantity,
        properties
      })
    });

    if (!response.ok) {
      throw new Error('Houderkeuze kon niet in het checkout-overzicht worden opgeslagen.');
    }
  }

  async function saveSelection(value, host) {
    await saveCheckoutLineProperty(value, host);
    await saveCartAttribute(value);
  }

  function setGate(host, blocked) {
    const scope = host.parentElement || document;

    scope.querySelectorAll('.cart__checkout-button').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (blocked) {
        if (!button.disabled) button.dataset.dovanaHolderDisabled = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (
        button.dataset.dovanaHolderDisabled === 'true' ||
        button.hasAttribute('data-dovana-holder-server-disabled')
      ) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        delete button.dataset.dovanaHolderDisabled;
        button.removeAttribute('data-dovana-holder-server-disabled');
      }
    });

    scope.querySelectorAll('.additional-checkout-buttons').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      if (blocked) {
        if (!element.hidden) element.dataset.dovanaHolderHidden = 'true';
        element.hidden = true;
      } else if (
        element.dataset.dovanaHolderHidden === 'true' ||
        element.hasAttribute('data-dovana-holder-server-hidden')
      ) {
        element.hidden = false;
        delete element.dataset.dovanaHolderHidden;
        element.removeAttribute('data-dovana-holder-server-hidden');
      }
    });

    host.dataset.dovanaValid = blocked ? 'false' : 'true';
  }

  async function syncExistingSelection(host, value, status) {
    if (!value || host.dataset.dovanaCheckoutSynced === value) return;

    setGate(host, true);
    status.textContent = 'Houderkeuze controleren…';
    status.dataset.state = 'saved';

    try {
      await saveCheckoutLineProperty(value, host);
      host.dataset.dovanaCheckoutSynced = value;
      status.textContent = 'Je houderkeuze is opgeslagen.';
      status.dataset.state = 'saved';
      setGate(host, false);
    } catch (_) {
      status.textContent = 'De houderkeuze kon niet voor de checkout worden opgeslagen. Probeer opnieuw.';
      status.dataset.state = 'error';
      setGate(host, true);
    }
  }

  async function renderHost(host) {
    if (!(host instanceof HTMLElement)) return;

    const count = Number.parseInt(host.dataset.count || '0', 10) || 0;
    let current = host.dataset.current || '';
    const renderKey = count + '|' + current + '|' + (host.dataset.line || '0');

    if (host.dataset.dovanaRenderKey === renderKey) return;
    host.dataset.dovanaRenderKey = renderKey;

    ensureStyles();

    if (count <= 0) {
      host.hidden = true;
      setGate(host, false);

      if (current) {
        try {
          await saveSelection('', host);
          host.dataset.current = '';
          host.dataset.dovanaRenderKey = '0||0';
        } catch (_) {}
      }

      return;
    }

    host.hidden = false;

    const wrapper = document.createElement('div');
    wrapper.className = 'dovana-holder-picker';

    const title = document.createElement('p');
    title.className = 'dovana-holder-picker__title';
    title.textContent = 'Kies je maandbloemhouders';
    wrapper.appendChild(title);

    const text = document.createElement('p');
    text.className = 'dovana-holder-picker__text';
    text.innerHTML =
      'Je hebt <strong>' +
      count +
      ' ' +
      (count === 1 ? 'maandbloem' : 'maandbloemen') +
      '</strong> in je winkelwagen. De houders zijn inbegrepen in de prijs.';
    wrapper.appendChild(text);

    const status = document.createElement('p');
    status.className = 'dovana-holder-picker__status';

    if (count === 1) {
      const choice = '1× enkele houder';

      const info = document.createElement('p');
      info.className = 'dovana-holder-picker__text';
      info.innerHTML = '<strong>Je ontvangt automatisch 1× een enkele houder.</strong>';
      wrapper.appendChild(info);

      status.textContent = 'Houderkeuze opslaan…';
      status.dataset.state = 'saved';
      wrapper.appendChild(status);

      host.replaceChildren(wrapper);
      setGate(host, true);

      try {
        if (current !== choice) {
          await saveSelection(choice, host);
          host.dataset.current = choice;
          host.dataset.dovanaRenderKey = '1|' + choice + '|' + (host.dataset.line || '0');
        } else {
          await saveCheckoutLineProperty(choice, host);
        }

        host.dataset.dovanaCheckoutSynced = choice;
        status.textContent = 'Geen extra keuze nodig.';
        status.dataset.state = 'saved';
        setGate(host, false);
      } catch (_) {
        status.textContent = 'De houderkeuze kon niet worden opgeslagen. Probeer de winkelwagen opnieuw te openen.';
        status.dataset.state = 'error';
        setGate(host, true);
      }

      return;
    }

    const options = combinations(count).map((parts) => combinationLabel(parts));
    const validCurrent = options.includes(current);

    if (current && !validCurrent) {
      try {
        await saveSelection('', host);
        current = '';
        host.dataset.current = '';
        delete host.dataset.dovanaCheckoutSynced;
      } catch (_) {}
    }

    const select = document.createElement('select');
    select.className = 'dovana-holder-picker__select';
    select.setAttribute('aria-label', 'Houdercombinatie voor maandbloemen');

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Kies een houdercombinatie';
    select.appendChild(placeholder);

    for (const label of options) {
      const option = document.createElement('option');
      option.value = label;
      option.textContent = label;
      select.appendChild(option);
    }

    if (validCurrent) select.value = current;
    wrapper.appendChild(select);

    status.textContent = validCurrent
      ? 'Je houderkeuze is opgeslagen.'
      : 'Kies eerst een houdercombinatie om verder te gaan.';
    status.dataset.state = validCurrent ? 'saved' : 'required';
    wrapper.appendChild(status);

    host.replaceChildren(wrapper);

    if (validCurrent) {
      await syncExistingSelection(host, current, status);
    } else {
      setGate(host, true);
    }

    select.addEventListener('change', async () => {
      const value = select.value;

      if (!value) {
        setGate(host, true);
        status.textContent = 'Kies eerst een houdercombinatie om verder te gaan.';
        status.dataset.state = 'required';

        try {
          await saveSelection('', host);
          host.dataset.current = '';
          delete host.dataset.dovanaCheckoutSynced;
        } catch (_) {}

        return;
      }

      select.disabled = true;
      setGate(host, true);
      status.textContent = 'Houderkeuze opslaan…';
      status.dataset.state = 'saved';

      try {
        await saveSelection(value, host);
        host.dataset.current = value;
        host.dataset.dovanaCheckoutSynced = value;
        host.dataset.dovanaRenderKey = count + '|' + value + '|' + (host.dataset.line || '0');
        status.textContent = 'Je houderkeuze is opgeslagen.';
        status.dataset.state = 'saved';
        setGate(host, false);
      } catch (_) {
        status.textContent = 'Opslaan is niet gelukt. Probeer je keuze opnieuw.';
        status.dataset.state = 'error';
        setGate(host, true);
      } finally {
        select.disabled = false;
      }
    });
  }

  function initAll() {
    document.querySelectorAll(HOST_SELECTOR).forEach((host) => {
      renderHost(host);
    });
  }

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      initAll();
    });
  });

  function start() {
    initAll();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-count', 'data-current', 'data-line']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

      const blockedHost = document.querySelector(
        HOST_SELECTOR + '[data-dovana-valid="false"]'
      );

      if (!(blockedHost instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const select = blockedHost.querySelector('select');
      if (select instanceof HTMLSelectElement) select.focus();
    },
    true
  );
})();